import type { StoreActionApi } from 'react-sweet-state';

import { fg } from '@atlaskit/platform-feature-flags';
import {
	ExperienceName,
	type ExperienceNameType,
} from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { addSentryBreadcrumb } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import { type SendAnalyticsEvent } from '@atlassian/conversation-assistant-instrumentation/types/analytics';
import { ASSISTANCE_SERVICE_ERROR_CODES } from '@atlassian/conversation-assistant-service-api/constants';
import {
	isAUPViolationError,
	isChannelNotFoundError,
	isHipaaError,
	isRateLimitedError,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { type StoreError } from '@atlassian/conversation-assistant-store/controllers/chat-signals';
import type {
	ChatStorePrivateState,
	Conversation,
	StoreActionLoggableName,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';


import { StreamErrorException } from './error-classes';
import type { ChatStoreAction, ChatStoreContainerProps } from './types';
import { clearStatus, createStoreError, isEmptyConversation } from './utils';

export type ErrorReason =
	| 'hipaa'
	| 'aupViolation'
	| 'channelNotFound'
	| 'noActiveConversation'
	| 'rateLimited'
	| 'generic';

type GetErrorReasonParams = {
	error: unknown;
	storeError: StoreError;
	currentConversationId: string;
	conversations: ChatStorePrivateState['conversations'];
};

const getErrorReason = ({
	error,
	storeError,
	currentConversationId,
	conversations,
}: GetErrorReasonParams): ErrorReason => {
	if (isHipaaError(error)) {
		return 'hipaa';
	}
	if (isAUPViolationError(error)) {
		return 'aupViolation';
	}
	if (isChannelNotFoundError(error)) {
		return 'channelNotFound';
	}
	if (!conversations?.[currentConversationId] || isEmptyConversation(currentConversationId)) {
		return 'noActiveConversation';
	}
	if (storeError.status_code === 429 || isRateLimitedError(error)) {
		return 'rateLimited';
	}
	return 'generic';
};

export type ActionErrorContext<Args = unknown> = {
	args: Args;
	error: unknown;
	storeError: StoreError;
	containerProps: ChatStoreContainerProps;
	errorReason: ErrorReason;
};
export type ActionErrorHandlerResult = void | { proceedToDefaultHandling: true };

/**
 * Error handler defined at action definition time (in createSafeAction options).
 *
 * Return type:
 * - `void` or `undefined`: Error was handled, skip default handling
 * - `{ proceedToDefaultHandling: true }`: Delegate to default handling
 *
 * @example
 * ```ts
 * createSafeAction(name, actionFn, {
 *   actionErrorHandler: (context) => {
 *     // Custom handling for this action
 *     return { proceedToDefaultHandling: true };
 *   },
 * });
 * ```
 */
export type ActionErrorHandler<Args = unknown> = (
	context: ActionErrorContext<Args>,
) => ActionErrorHandlerResult;

/**
 * Error callback for action invocation time (passed in action args).
 * This type omits the `onError` argument from the error handler's arguments to prevent recursive invocation.
 * Use `OnErrorCallback<ActionArgsType>` when defining `onError` arg.
 *
 * @example
 * ```ts
 * // In args type definition:
 * type MyActionArgs = {
 *   someParam: string;
 *   onError?: OnErrorCallback<MyActionArgs>;
 * };
 *
 * // At invocation:
 * myAction({
 *   someParam: 'value',
 *   onError: ({ error, errorReason, args }) => {
 *     // Custom handling defined by invoker
 *     return { proceedToDefaultHandling: true };
 *   },
 * });
 * ```
 */
export type OnErrorCallback<Args> = (
	context: ActionErrorContext<Omit<Args, 'onError'>>,
) => ActionErrorHandlerResult;

export type StoreErrorHandlerProps<Args> = {
	args: Args;
	api: StoreActionApi<ChatStorePrivateState>;
	actionLoggableName: StoreActionLoggableName;
	containerProps: ChatStoreContainerProps;
	error: unknown;
	setConversationData: (params: {
		conversationId: string;
		data: Partial<Conversation>;
	}) => ChatStoreAction;
	experienceName?: (args: Args) => ExperienceNameType;
	actionErrorHandler?: ActionErrorHandler<Args>;
	getRateLimitState?: (args: Args) => { isDeepResearchAttempt?: boolean };
};

const reportChatError = (
	sendAnalyticsEvent: SendAnalyticsEvent,
	reason: ErrorReason,
	storeError: StoreError,
) => {
	if (!fg('ai_mate_unhandled_error_reporting')) {
		return;
	}

	sendAnalyticsEvent({
		eventType: 'operational',
		action: 'unhandledError',
		actionSubject: 'aiMate',
		attributes: {
			reason,
			statusCode: storeError.status_code,
			messageTemplate: storeError.message_template,
			errorType: storeError.errorType,
			traceId: storeError.traceId,
			errorContent: storeError.content,
			...(storeError.storeActionExecutionError
				? {
					actionLoggableName: storeError.storeActionExecutionError.actionLoggableName,
				}
				: {}),
		},
	});
};

/**
 * Centralized error handler for chat store actions.
 *
 * @typeParam Args - The type of the action arguments
 *
 * @param props - Configuration for error handling, including the original action args,
 *   store API, and optional custom error handler via `actionErrorHandler`
 *
 * @remarks
 * Implements a three-phase error handling pipeline:
 * 1. **Mandatory checks** - Critical errors that cannot be overridden (e.g., compliance violations)
 * 2. **Action-specific handling** - Custom error handling via `actionErrorHandler` prop
 * 3. **Default handlers** - Standard error handling based on categorized error reasons
 */
export const storeErrorHandler = <Args>(
	props: StoreErrorHandlerProps<Args>,
): void => {
	const {
		args,
		api,
		actionLoggableName,
		error,
		containerProps,
		actionErrorHandler,
		setConversationData,
	} = props;
	const { getState, setState, dispatch } = api;
	addSentryBreadcrumb(`createSafeAction: ${actionLoggableName} errored`);

	const storeError = createStoreError(error, undefined, actionLoggableName);
	const errorReason = getErrorReason({
		error,
		storeError,
		currentConversationId: getState().currentConversationId,
		conversations: getState().conversations,
	});

	// ============================================
	// PHASE 1: Mandatory checks (cannot be overridden)
	// ============================================

	// Handle experience tracking
	// Rate limits are ignored because they trigger a retry, not an experience termination
	if (props?.experienceName && errorReason !== 'rateLimited') {
		if (errorReason === 'aupViolation') {
			const aupError = error as {
				detail: { message: { message_template: string } };
				errorAttributes: Record<string, unknown>;
			};
			containerProps.experienceTracker.abort({
				name: ExperienceName.AIFC_MESSAGE,
				reason: aupError.detail.message.message_template,
				attributes: {
					...aupError.errorAttributes,
					errorType: error instanceof StreamErrorException ? 'StreamErrorException' : '',
				},
			});
		} else {
			containerProps.experienceTracker.fail({
				name: props.experienceName(args),
				error: error instanceof Error ? error : new Error(String(error)),
			});
		}
	}

	// Handle restricted use errors
	if (errorReason === 'hipaa') {
		containerProps.setConversationError({
			type: 'hipaa',
			underlyingError: storeError,
		});
		reportChatError(containerProps.sendAnalyticsEvent, 'hipaa', storeError);
		return;
	}

	// ============================================
	// PHASE 2: Action-specific error handling (optional)
	// ============================================

	if (actionErrorHandler) {
		const result = actionErrorHandler({
			args,
			error,
			storeError,
			containerProps,
			errorReason,
		});
		if (!result?.proceedToDefaultHandling) {
			return;
		}
	}

	// ============================================
	// PHASE 3: Default error handlers
	// ============================================

	if (errorReason === 'channelNotFound') {
		containerProps.setConversationError({
			type: 'conversationNotFound',
			underlyingError: storeError,
		});
		reportChatError(containerProps.sendAnalyticsEvent, 'channelNotFound', storeError);
		return;
	}

	if (errorReason === 'noActiveConversation') {
		containerProps.setConversationError({
			type: 'conversationEmptyOrNotPresentInStore',
			underlyingError: storeError,
		});
		reportChatError(containerProps.sendAnalyticsEvent, 'noActiveConversation', storeError);
		if (storeError.message_template === ASSISTANCE_SERVICE_ERROR_CODES.ROVO_PRODUCT_NOT_ACTIVE) {
			containerProps.setRovoEnabled(false, storeError.message_template);
		}
		return;
	}

	const currentConversationId = getState().currentConversationId;

	if (errorReason === 'rateLimited') {
		const rateLimitState = props?.getRateLimitState?.(args) ?? {};
		setState({
			conversations: {
				...getState().conversations,
				[currentConversationId]: {
					...getState().conversations[currentConversationId],
					...clearStatus(),
				},
			},
		});
		containerProps.setConversationError({
			type: 'rateLimited',
			underlyingError: storeError,
			rateLimitedActionType: rateLimitState.isDeepResearchAttempt ? 'deep-research' : 'generic',
		});
		reportChatError(containerProps.sendAnalyticsEvent, 'rateLimited', storeError);
		return;
	}

	dispatch(
		setConversationData({
			conversationId: currentConversationId,
			data: {
				...clearStatus(),
			},
		}),
	);

	containerProps.setMessages(currentConversationId, [
		...containerProps.getMessages(currentConversationId),
		storeError,
	]);

	const errorAttributes = (error as { errorAttributes?: Record<string, unknown> } | undefined)
		?.errorAttributes;

	containerProps.experienceTracker.fail({
		name: ExperienceName.AIFC_MESSAGE,
		error: error instanceof Error ? error : new Error(String(error)),
		attributes: {
			latestStreamMessageType: errorAttributes?.latestStreamMessageType,
			latestStreamMessageMillisOffset: errorAttributes?.latestStreamMessageMillisOffset,
		},
	});

	reportChatError(containerProps.sendAnalyticsEvent, 'generic', storeError);
};
